<template>
    <div class="card mb-3" style="min-height: 60vh;">
      <div class="card-body">
        <div>
  
          <h3>POS Database Recovery</h3>
  
          <a-spin :spinning="loading">
            <div class="col-md-12" style="padding: 30px; height: 64vh; overflow-y: auto; padding-bottom: 90px;">
  
              <a-steps class="steps-navigator" :current="currentTab" @change="onChangeTab">
                <a-step title="Step 1" description="Select Account, Site and Terminal to Create Recovery Tables" :disabled='currentTab == maxTab' />
                <a-step title="Step 2" description="Download Recovery Tables" :disabled='currentTab == maxTab' />
              </a-steps>
  
              <div class="col-md-12" v-if="currentTab == 0">
                <div class="col-md-12">
                  <base-input label="Account Selection:">
                    <a-select showSearch
                              style="width: 100%"
                              placeholder=""
                              optionFilterProp="children"
                              v-model="selectedAccount">
                      <a-select-option v-for="(account, index) in accounts"
                                        :key="index"
                                        :value="account.id">
                        {{account.account_name}}
                      </a-select-option>
                    </a-select>
                  </base-input>
                </div>
  
                <div class="col-md-12">
                  <base-input label="Site:">
                    <a-select showSearch
                              style="width: 100%"
                              placeholder=""
                              optionFilterProp="children"
                              v-model="selectedSite">
                      <a-select-option v-for="(site, index) in sites"
                                        :key="index"
                                        :value="site.id">
                        {{site.name}}
                      </a-select-option>
                    </a-select>
                  </base-input>
                </div>
  
                <br/>
  
                <div class="col-md-12">
                  <base-input label="Terminal:">
                    <a-select showSearch
                              style="width: 100%"
                              placeholder=""
                              optionFilterProp="children"
                              v-model="selectedTerminal">
                      <a-select-option v-for="(terminal, index) in terminals"
                                        :key="index"
                                        :value="terminal.id">
                        {{terminal.name}}
                        <a-tag v-if="terminal.is_for_recovery" color="green" style="margin-left: 6px;">Recovery</a-tag>
                      </a-select-option>
                    </a-select>
                  </base-input>
                </div>

                <div class="col-md-12" v-if="selectedTerminal">
                  <div style="display: flex; align-items: center; gap: 10px; margin-top: 4px;">
                    <span>Terminal is flagged for POS recovery:</span>
                    <a-tag :color="selectedTerminalIsForRecovery ? 'green' : 'red'">
                      {{ selectedTerminalIsForRecovery ? 'Enabled' : 'Disabled' }}
                    </a-tag>
                    <a-switch
                      :checked="selectedTerminalIsForRecovery"
                      :loading="flaggingRecovery"
                      checked-children="On"
                      un-checked-children="Off"
                      @change="toggleRecoveryFlag" />
                  </div>
                  <small v-if="!selectedTerminalIsForRecovery" style="color: #cf1322;">
                    Only terminals flagged for recovery can generate recovery tables.
                  </small>
                </div>

              </div>
  
              <div class="col-md-12" v-if="currentTab == 1">

                <a-list bordered>
                  <div slot="header">Click to download.</div>
                  <a-list-item>
                    <a @click.prevent="downloadFile(recoveryZipUrl)">
                      <a-button type="primary" icon="download" size="default">
                        Download {{ recoveryFileName }}
                      </a-button>
                    </a>
                  </a-list-item>
                </a-list>

                <div style="margin-top: 16px;" v-if="recoveryCounts && Object.keys(recoveryCounts).length">
                  <h5>Exported record counts</h5>
                  <a-table
                    :columns="countColumns"
                    :dataSource="countRows"
                    :pagination="false"
                    size="small"
                    rowKey="table" />

                  <div style="margin-top: 12px;">
                    <a-button type="default" icon="check-circle" :loading="validating" @click="validateRecovery">
                      Validate Recovered Document Count
                    </a-button>
                    <a-tag
                      v-if="validationResult"
                      :color="validationResult.matched ? 'green' : 'red'"
                      style="margin-left: 10px;">
                      {{ validationResult.message }}
                    </a-tag>
                  </div>
                </div>

              </div>
            </div>
          </a-spin>
  
          <div class="col-md-12" style="position: absolute; bottom: 0px; width: 98%; background-color: #f2f2f2 ; padding: 10px;">
            <div class="steps-action float-right">
                <a-button
                  v-if="currentTab > 0 && currentTab == maxTab"
                  @click="prevStep()"
                  :disabled='loading'>
                  Back
                </a-button>
  
                <a-button
                  v-if="currentTab < maxTab && currentTab != 1"
                  type="primary"
                  @click="proceed()"
                  style="margin-left: 8px"
                  :disabled='loading'>
                  Generate Recovery Tables
                </a-button>
            </div>
          </div>
  
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import { mapState } from 'vuex';
  import HTTPRequest from '../../controllers/httpRequest.js';
  import secureStorage from '../../controllers/secureStorage.js';
  import axios from "axios";
  
  const defaultData = {
    loading: false,
    currentTab: 0,
    accounts: [],
    sites: [],
    terminals: [],
    selectedAccount: null,
    selectedSite: null,
    selectedTerminal: null,
    selectedInfo: {},
    requestData: {},
    password: '',
    resetOutput: {},
    recoveryZipUrl: null,
    recoveryFileName: '',
    recoveryCounts: {},
    documentCount: 0,
    validating: false,
    validationResult: null,
    flaggingRecovery: false
  }

  export default{
    data() {
      return {
        maxTab: 1,
        countColumns: [
          { title: 'Table', dataIndex: 'table', key: 'table' },
          { title: 'Record Count', dataIndex: 'count', key: 'count' }
        ],
        ...defaultData
      }
    },
    computed: {
      ...mapState(['endPoint']),
      selectedTerminalObject() {
        return this.terminals.find(t => t.id == this.selectedTerminal) || null;
      },
      selectedTerminalIsForRecovery() {
        return !!(this.selectedTerminalObject && this.selectedTerminalObject.is_for_recovery);
      },
      countRows() {
        return Object.keys(this.recoveryCounts || {}).map(key => ({
          table: key,
          count: this.recoveryCounts[key]
        }));
      }
    },
    watch: {
      selectedAccount: function() {
        if (!this.selectedAccount) { return; }
  
        this.selectedSite = null;
        this.populateSites();
      },
      selectedSite: function() {
        this.terminals = [];
        this.selectedTerminal = null;
  
        if (this.selectedSite) {
          this.populateTerminals();
        }
      }
    },
    mounted: async function() {
      await this.populateAccounts();
    },
    methods: {
      onChangeTab(current) {
        const hasErrors = this.isNotValid();
        if (hasErrors) { return; }
  
        this.currentTab = current;
      },
  
      prevStep() {
        this.currentTab--;
      },
  
      nextStep() {
        //Arvin
        const hasErrors = this.isNotValid();
        if (hasErrors) { return; }
  
        if (this.currentTab + 1 == 1) { this.setSelectedInfo(); }
  
        this.currentTab++;
      },
  
      async resetAll() {
        Object.assign(this, { ...defaultData });
        await this.populateAccounts();
      },
  
      renderMsg(type, msg, desc) {
        this.$notification[type]({ message: msg, description: desc, });
      },
  
      async populateAccounts() {
        // Get all Valid and Active accounts
        this.loading = true;
        let accounts = await HTTPRequest.getRequest(`${this.endPoint}accounts/get_valid_accounts`, null, null)
        accounts = accounts.sort((a, b) => {
          const textA = a.account_name.toUpperCase();
          const textB = b.account_name.toUpperCase();
          return (textA < textB) ? -1 : (textA > textB) ? 1 : 0;
        });
        this.accounts = accounts || [];
        this.loading = false;
      },
  
      async populateSites() {
        if (!this.selectedAccount) { return; }
        this.loading = true;
        const sites = await HTTPRequest.getRequest(`${this.endPoint}sites/get_sites_with_terminal?account_id=${this.selectedAccount}`, null, null)
        this.sites = sites || [];
        this.loading = false;
      },
  
      async populateTerminals() {
        this.loading = true;
        const terminals = await HTTPRequest.getRequest(`${this.endPoint}terminals?account_id=${this.selectedAccount}&site_id=${this.selectedSite}`, null, null)
        this.terminals = (terminals || {}).data || [];
        this.loading = false;
      },
  
      setSelectedInfo() {
        const selectedAccount = this.accounts.filter(account => account.id == this.selectedAccount)[0];
        const selectedSite = this.sites.filter(site => site.id == this.selectedSite)[0];
        const selectedTerminal = this.terminals.filter(terminal => terminal.id == this.selectedTerminal)[0];
  
        this.selectedInfo = {
          accountName: selectedAccount.account_name,
          siteName: selectedSite.name,
          terminal: selectedTerminal.name
        }
      },
  
      getRequestData() {
        this.requestData.account_id = this.selectedAccount;
        this.requestData.site_id = this.selectedSite;
        this.requestData.terminal_id = this.selectedTerminal;

        // Getting account name
        const selectedAccount = this.accounts.filter(account => account.id == this.selectedAccount)[0];
        // this.requestData.account_name = selectedAccount.account_name;
  
        // Getting site code
        const selectedSite = this.sites.filter(site => site.id == this.selectedSite)[0];
        // this.requestData.site_code = selectedSite.code;
  
        // Getting selected Terminal data
        const selectedTerminal = this.terminals.filter(terminal => terminal.id == this.selectedTerminal)[0];
        // this.requestData.terminal_code = selectedTerminal.code;
      },
  
      isNotValid: function() {
        let withError = false;
        if (!this.selectedAccount) {
          this.renderMsg('error', 'Error', 'Please Select an Account');
          withError = true;
        } else if (!this.selectedSite) {
          this.renderMsg('error', 'Error', 'Please Select a Site');
          withError = true;
        } else if (!this.selectedTerminal) {
          this.renderMsg('error', 'Error', 'Please Select a Terminal');
          withError = true;
        } else if (!this.selectedTerminalIsForRecovery) {
          this.renderMsg('error', 'Error', 'This terminal is not flagged for POS recovery. Enable the recovery flag first.');
          withError = true;
        }
  
        return withError;
      },
  
      createLogs: async function (outputData) {
        const endPoint = `${this.endPoint}admin/create_admin_logs?feature=pos_database_recovery`;
        let data = { logs: { ...this.requestData, ...outputData } }
        HTTPRequest.postRequest(endPoint, null, data);
      },
  
      proceed: async function() {
        if (this.isNotValid()) { return }
        this.loading = true;
        this.getRequestData();
        const _this = this;
        const config = {
          method: 'POST',
          url: `${this.endPoint}accounts/generate_pos_recovery`,
          headers: {'Authorization': 'Bearer ' + secureStorage.getItem("accessToken"), 'Request-Source':"BXI-File", 'ip':secureStorage.getItem("ipAddress")},
          data: this.requestData
        };
        await axios(config)
        .then(function (response) {
          if (response.data['error']) {
            _this.renderMsg('error', 'Creating Recovery Tables Failed', response.data['error']);
          } else {
            _this.currentTab++;
            _this.renderMsg('success', 'Success Creating Recovery Tables', 'Creating Recovery Tables is successful.');
            _this.createLogs(response.data);
            const files = response.data['data'] || [];
            _this.recoveryZipUrl = files[0] || null;
            _this.recoveryFileName = response.data['file_name'] || (_this.recoveryZipUrl ? _this.recoveryZipUrl.split('/').pop() : 'recovery.zip');
            _this.recoveryCounts = response.data['counts'] || {};
            _this.documentCount = response.data['document_count'] || 0;
            _this.validationResult = null;
          }
        })
        .catch(function (error) {
          _this.renderMsg('error', 'Error', "Something Went Wrong on Creating Recovery Tables.")
        });
        this.loading = false;
      },

      toggleRecoveryFlag: async function(checked) {
        if (!this.selectedTerminal) { return; }
        this.flaggingRecovery = true;
        try {
          const payload = { terminal: { is_for_recovery: checked } };
          const res = await HTTPRequest.putRequest(`${this.endPoint}terminals/${this.selectedTerminal}`, null, payload);
          if (res && res.errors == undefined) {
            // Reflect the new flag locally so the indicator and validation update.
            const idx = this.terminals.findIndex(t => t.id == this.selectedTerminal);
            if (idx > -1) { this.$set(this.terminals[idx], 'is_for_recovery', checked); }
            this.renderMsg('success', 'Updated', `Recovery flag ${checked ? 'enabled' : 'disabled'} for this terminal.`);
          } else {
            this.renderMsg('error', 'Error', 'Failed to update the recovery flag.');
          }
        } catch (e) {
          this.renderMsg('error', 'Error', 'Failed to update the recovery flag.');
        }
        this.flaggingRecovery = false;
      },

      validateRecovery: async function() {
        this.validating = true;
        this.validationResult = null;
        try {
          const result = await HTTPRequest.getRequest(
            `${this.endPoint}accounts/validate_pos_recovery?account_id=${this.selectedAccount}&site_id=${this.selectedSite}&terminal_id=${this.selectedTerminal}&expected_document_count=${this.documentCount}`,
            null, null
          );
          this.validationResult = result || null;
          if (result) {
            this.renderMsg(result.matched ? 'success' : 'warning', result.matched ? 'Validation Passed' : 'Count Mismatch', result.message);
          }
        } catch (e) {
          this.renderMsg('error', 'Error', 'Failed to validate recovered document count.');
        }
        this.validating = false;
      },

      downloadFile: async function(url) {
        if (!url) {
          this.renderMsg('error', 'Download Failed', 'No recovery file available to download.');
          return;
        }
        const _this = this;
        this.loading = true;
        const fileName = this.recoveryFileName || url.split('/').pop();
        try {
          const response = await axios({
            method: 'GET',
            url: `${this.endPoint}accounts/download_pos_recovery`,
            params: {
              account_id: this.selectedAccount,
              site_id: this.selectedSite,
              terminal_id: this.selectedTerminal
            },
            headers: {
              'Authorization': 'Bearer ' + secureStorage.getItem("accessToken"),
              'Request-Source': 'BXI-File',
              'ip': secureStorage.getItem("ipAddress")
            },
            responseType: 'blob'
          });
          const blob = new Blob([response.data], { type: 'application/zip' });
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(link.href);
          _this.renderMsg('success', 'Download Complete', `${fileName} downloaded successfully.`);
        } catch (error) {
          _this.renderMsg('error', 'Download Failed', `Failed to download ${fileName}. Please try again.`);
        }
        this.loading = false;
      }
    }
  }
  </script>
  
  <style scoped>
  .steps-navigator {
    margin-bottom: 20px;
  }
  
  p.header {
    font-size: 14px;
  }
  
  .selectedInfo-cont {
    display: inline-block;
    margin-left: 10px;
  }
  
  .selectedInfo-cont div {
    margin-bottom: 10px;
  }
  </style>
  